import { useRoute } from 'vue-router'

export interface PageContext {
  route: {
    path: string
    name: string | null
    params: Record<string, any>
    query: Record<string, any>
  }
  pageType: string | null
  entityType: string | null
  entityId: string | null
  entityData: Record<string, any> | null
}

// Global state to store page context data provided by pages
const pageContextData = ref<Record<string, any> | null>(null)

/**
 * Composable to gather context about the current page
 * This helps the AI understand what the user is currently viewing
 */
export const usePageContext = () => {
  const route = useRoute()
  
  /**
   * Provide context data from a page component
   * Pages should call this to expose their data to the AI chat
   */
  const providePageContext = (data: Record<string, any>) => {
    pageContextData.value = data
  }

  /**
   * Clear page context data
   */
  const clearPageContext = () => {
    pageContextData.value = null
  }
  
  const getPageContext = (): PageContext => {
    const context: PageContext = {
      route: {
        path: route.path,
        name: route.name as string | null,
        params: { ...route.params },
        query: { ...route.query }
      },
      pageType: null,
      entityType: null,
      entityId: null,
      entityData: pageContextData.value
    }

    // Determine page type and entity information based on route
    const path = route.path
    
    // Project details page: /company/[company_public_id]/projects/[project_id]
    if (path.match(/\/company\/[^/]+\/projects\/[^/]+$/)) {
      context.pageType = 'project_details'
      context.entityType = 'project'
      context.entityId = route.params.project_id as string
    }
    // Projects list page: /company/[company_public_id]/projects
    else if (path.match(/\/company\/[^/]+\/projects$/)) {
      context.pageType = 'projects_list'
      context.entityType = 'project'
    }
    // Competitor details page: /company/[company_public_id]/competitors/[competitor_id]
    else if (path.match(/\/company\/[^/]+\/competitors\/[^/]+$/)) {
      context.pageType = 'competitor_details'
      context.entityType = 'competitor'
      context.entityId = route.params.competitor_id as string
    }
    // Competitors list page: /company/[company_public_id]/competitors
    else if (path.match(/\/company\/[^/]+\/competitors$/)) {
      context.pageType = 'competitors_list'
      context.entityType = 'competitor'
    }
    // Insights page: /company/[company_public_id]/insights
    else if (path.match(/\/company\/[^/]+\/insights/)) {
      context.pageType = 'insights'
      context.entityType = 'insight'
    }
    // Foundation page: /company/[company_public_id]/foundation
    else if (path.match(/\/company\/[^/]+\/foundation$/)) {
      context.pageType = 'foundation'
      context.entityType = 'company'
    }
    // Company overview: /company/[company_public_id]
    else if (path.match(/\/company\/[^/]+$/)) {
      context.pageType = 'company_overview'
      context.entityType = 'company'
    }

    return context
  }

  /**
   * Get a human-readable description of the current page context
   * This is used to provide context to the AI
   */
  const getContextDescription = (): string => {
    const context = getPageContext()
    const parts: string[] = []

    if (context.pageType === 'project_details') {
      if (context.entityData?.project) {
        const project = context.entityData.project
        parts.push(`The user is currently viewing the project details page for "${project.title}" (ID: ${context.entityId}).`)
        if (project.status) {
          parts.push(`The project status is: ${project.status}.`)
        }
        if (project.description) {
          parts.push(`Project description: ${project.description}`)
        }
      } else {
        parts.push(`The user is currently viewing a project details page (Project ID: ${context.entityId}).`)
      }
    } else if (context.pageType === 'projects_list') {
      parts.push('The user is currently viewing the projects list page.')
    } else if (context.pageType === 'competitor_details') {
      if (context.entityData?.competitor) {
        const competitor = context.entityData.competitor
        parts.push(`The user is currently viewing the competitor details page for "${competitor.name}" (ID: ${context.entityId}).`)
      } else {
        parts.push(`The user is currently viewing a competitor details page (Competitor ID: ${context.entityId}).`)
      }
    } else if (context.pageType === 'competitors_list') {
      parts.push('The user is currently viewing the competitors list page.')
    } else if (context.pageType === 'insights') {
      parts.push('The user is currently viewing the insights page.')
    } else if (context.pageType === 'foundation') {
      parts.push('The user is currently viewing the company foundation page.')
    } else if (context.pageType === 'company_overview') {
      parts.push('The user is currently viewing the company overview page.')
    }

    return parts.join(' ')
  }

  return {
    getPageContext,
    getContextDescription,
    providePageContext,
    clearPageContext
  }
}

